import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, ImageUp, RefreshCw, ScanLine, Search, Square, Ticket, XCircle } from "lucide-react";
import QrScanner from "qr-scanner";
import { api } from "@/lib/api";

const emptySummary = { total: 0, valid: 0, used: 0, cancelled: 0 };

function cameraErrorMessage(error) {
  const message = String(error?.message || error || "").toLowerCase();
  if (message.includes("notallowed") || message.includes("permission") || message.includes("denied")) {
    return "El navegador bloqueó la cámara. Permítela en la configuración del sitio y vuelve a intentar.";
  }
  if (message.includes("notfound") || message.includes("devicesnotfound")) {
    return "No encontramos una cámara disponible en este dispositivo.";
  }
  if (message.includes("notreadable") || message.includes("trackstart")) {
    return "Otra aplicación está usando la cámara. Ciérrala y vuelve a intentar.";
  }
  return "No se pudo iniciar la cámara. También puedes escanear una foto del QR.";
}

export default function StaffTickets() {
  const staffPin = sessionStorage.getItem("cg_staff_pin") || "";
  const [summary, setSummary] = useState(emptySummary);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [result, setResult] = useState(null);
  const [scannerStatus, setScannerStatus] = useState("idle");
  const [scannerError, setScannerError] = useState("");
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const processingRef = useRef(false);
  const scanLockRef = useRef(false);

  const load = useCallback(async (term = "") => {
    try {
      const [summaryResponse, listResponse] = await Promise.all([
        api.get("/tickets/staff/summary", { headers: { "x-staff-pin": staffPin } }),
        api.get("/tickets/staff/list", { params: { search: term }, headers: { "x-staff-pin": staffPin } }),
      ]);
      setSummary(summaryResponse.data);
      setTickets(listResponse.data);
    } catch {
      setResult({ result: "invalid", message: "No se pudo consultar la boletera. Vuelve a iniciar sesión." });
    }
  }, [staffPin]);

  const validate = useCallback(async (token) => {
    if (!token || processingRef.current) return;
    processingRef.current = true;
    try {
      const response = await api.post(
        "/tickets/staff/validate",
        { token, checked_in_by: "Portal Staff" },
        { headers: { "x-staff-pin": staffPin } }
      );
      setResult(response.data);
      setManualCode("");
      await load("");
    } catch (error) {
      setResult({ result: "invalid", message: error.response?.data?.detail || "No se pudo validar el boleto." });
    } finally {
      processingRef.current = false;
    }
  }, [load, staffPin]);

  const stopScanner = useCallback((updateStatus = true) => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { scanner.stop(); } catch {}
      try { scanner.destroy(); } catch {}
    }
    if (updateStatus) setScannerStatus("idle");
  }, []);

  const handleDecoded = useCallback(async (decodedText) => {
    if (!decodedText || scanLockRef.current) return;
    scanLockRef.current = true;
    stopScanner(false);
    setScannerStatus("idle");
    await validate(decodedText);
  }, [stopScanner, validate]);

  const startScanner = useCallback(async () => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setScannerError("La cámara requiere HTTPS y un navegador compatible.");
      setScannerStatus("error");
      return;
    }
    stopScanner(false);
    setResult(null);
    setScannerError("");
    setScannerStatus("starting");
    scanLockRef.current = false;
    processingRef.current = false;

    try {
      if (!videoRef.current) throw new Error("Video element not found");
      const hasCamera = await QrScanner.hasCamera();
      if (!hasCamera) throw new Error("NotFoundError");
      const scanner = new QrScanner(
        videoRef.current,
        (scanResult) => { void handleDecoded(scanResult.data); },
        {
          preferredCamera: "environment",
          maxScansPerSecond: 20,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          returnDetailedScanResult: true,
          onDecodeError: () => {},
        }
      );
      scannerRef.current = scanner;
      scanner.setInversionMode("both");
      await scanner.start();
      setScannerStatus("scanning");
    } catch (error) {
      stopScanner(false);
      setScannerError(cameraErrorMessage(error));
      setScannerStatus("error");
    }
  }, [handleDecoded, stopScanner]);

  const scanImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    stopScanner(false);
    setResult(null);
    setScannerError("");
    setScannerStatus("starting");
    scanLockRef.current = true;
    try {
      const scanResult = await QrScanner.scanImage(file, {
        returnDetailedScanResult: true,
        alsoTryWithoutScanRegion: true,
      });
      setScannerStatus("idle");
      await validate(scanResult.data);
    } catch {
      stopScanner(false);
      scanLockRef.current = false;
      setScannerError("No se encontró un QR legible en la imagen seleccionada.");
      setScannerStatus("error");
    }
  };

  const validateManual = async (event) => {
    event.preventDefault();
    if (!manualCode.trim()) return;
    scanLockRef.current = true;
    stopScanner(false);
    setScannerStatus("idle");
    await validate(manualCode.trim());
  };

  const nextScan = () => {
    setResult(null);
    setScannerError("");
    processingRef.current = false;
    scanLockRef.current = false;
    window.setTimeout(() => { void startScanner(); }, 50);
  };

  useEffect(() => { load(""); }, [load]);
  useEffect(() => () => { stopScanner(false); }, [stopScanner]);

  const statusStyle = result?.result === "accepted"
    ? "border-green-300 bg-green-50 text-green-900"
    : result?.result === "used"
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : "border-red-300 bg-red-50 text-red-900";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Emitidos", summary.total], ["Disponibles", summary.valid], ["Ingresaron", summary.used], ["Cancelados", summary.cancelled]].map(([label, value]) => (
          <div key={label} className="card-cg bg-white p-4">
            <div className="overline text-[var(--brand-muted)]">{label}</div>
            <div className="font-serif-cg text-3xl mt-2">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-6 items-start">
        <section className="card-cg bg-white p-5 md:p-6">
          <div className="flex items-center gap-3">
            <ScanLine className="text-[var(--brand-primary)]" />
            <div><div className="overline text-[var(--brand-primary)]">Control de acceso</div><h2 className="font-serif-cg text-2xl">Escanear boleto</h2></div>
          </div>

          {!result ? (
            <div className="mt-5">
              <div className={`relative overflow-hidden rounded-md bg-black ${scannerStatus === "idle" || scannerStatus === "error" ? "hidden" : "block"}`}>
                <video ref={videoRef} className="aspect-[4/3] w-full object-cover" muted playsInline />
                {scannerStatus === "starting" ? <div className="absolute inset-0 grid place-items-center bg-black/50 text-sm text-white">Iniciando cámara...</div> : null}
              </div>
              {scannerStatus === "starting" ? <p className="mt-3 text-sm text-[var(--brand-muted)]">Iniciando cámara...</p> : null}
              {scannerStatus === "scanning" ? <p className="mt-3 text-sm text-green-700">Lector activo. Mantén el QR dentro del recuadro; se validará automáticamente.</p> : null}
              {scannerError ? <div className="mt-3 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">{scannerError}</div> : null}
              <div className="flex flex-wrap gap-2 mt-4">
                {scannerStatus === "scanning" || scannerStatus === "starting" ? (
                  <button type="button" onClick={() => { stopScanner(); }} className="btn-secondary-cg"><Square size={15} /> Detener cámara</button>
                ) : (
                  <button type="button" onClick={() => { void startScanner(); }} className="btn-primary-cg"><Camera size={16} /> Abrir cámara</button>
                )}
                <label className="btn-secondary-cg cursor-pointer"><ImageUp size={16} /> Escanear imagen<input type="file" accept="image/*" capture="environment" onChange={scanImage} className="sr-only" /></label>
              </div>
            </div>
          ) : null}

          {result ? (
            <div className={`mt-5 border p-5 ${statusStyle}`}>
              <div className="flex items-start gap-3">
                {result.result === "accepted" ? <CheckCircle2 size={28} /> : result.result === "used" ? <AlertTriangle size={28} /> : <XCircle size={28} />}
                <div>
                  <h3 className="font-serif-cg text-2xl">{result.message}</h3>
                  {result.ticket ? (
                    <div className="mt-3 text-sm space-y-1">
                      <p><strong>{result.ticket.buyer_name}</strong></p>
                      <p>{result.ticket.ticket_type_name} · {result.ticket.code}</p>
                      <p>{result.ticket.buyer_email}</p>
                      {result.ticket.buyer_phone ? <p>{result.ticket.buyer_phone}</p> : null}
                      <p className="text-xs">Orden: {result.ticket.order_id}</p>
                      {result.ticket.checked_in_at ? <p>Acceso: {new Date(result.ticket.checked_in_at).toLocaleString("es-MX")}</p> : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <button type="button" onClick={nextScan} className="btn-primary-cg mt-5"><ScanLine size={15} /> Escanear siguiente</button>
            </div>
          ) : null}

          <form onSubmit={validateManual} className="border-t border-[var(--brand-border)] mt-6 pt-5">
            <label className="block">
              <span className="overline text-[var(--brand-muted)]">Código manual</span>
              <div className="flex mt-2">
                <input value={manualCode} onChange={(event) => setManualCode(event.target.value.toUpperCase())} placeholder="CG26-XXXXXXXX" className="min-w-0 flex-1 border border-[var(--brand-border)] px-3 py-2.5 font-mono outline-none focus:border-[var(--brand-primary)]" />
                <button type="submit" className="btn-primary-cg">Validar</button>
              </div>
            </label>
          </form>
        </section>

        <section className="card-cg bg-white p-5 md:p-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><div className="overline text-[var(--brand-primary)]">Asistentes</div><h2 className="font-serif-cg text-2xl">Lista de boletos</h2></div>
            <button type="button" onClick={() => load(search)} className="btn-secondary-cg"><RefreshCw size={14} /> Actualizar</button>
          </div>
          <form onSubmit={(event) => { event.preventDefault(); load(search); }} className="flex mt-5">
            <div className="relative flex-1"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, correo o código" className="w-full border border-[var(--brand-border)] pl-10 pr-3 py-2.5 outline-none focus:border-[var(--brand-primary)]" /></div>
            <button type="submit" className="btn-primary-cg">Buscar</button>
          </form>
          <div className="mt-5 max-h-[620px] overflow-y-auto divide-y divide-[var(--brand-border)]">
            {tickets.map((ticketItem) => (
              <article key={ticketItem.id} className="py-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{ticketItem.buyer_name}</h3>
                  <p className="text-xs text-[var(--brand-muted)] truncate">{ticketItem.buyer_email}</p>
                  <p className="text-xs mt-1">{ticketItem.ticket_type_name} · <span className="font-mono">{ticketItem.code}</span></p>
                </div>
                <span className={`shrink-0 px-2 py-1 text-[10px] uppercase tracking-wider border ${ticketItem.status === "valid" ? "border-green-300 text-green-800" : ticketItem.status === "used" ? "border-amber-300 text-amber-800" : "border-red-300 text-red-800"}`}>{ticketItem.status}</span>
              </article>
            ))}
            {!tickets.length ? <div className="py-10 text-center text-sm text-[var(--brand-muted)]"><Ticket className="mx-auto mb-3" />No se encontraron boletos.</div> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
